import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, LogIn } from 'lucide-react';
import { Button } from '../ui/button';
import { useSiteContent } from '../../contexts/SiteContentContext';

const AgencyNavbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  const { settings } = useSiteContent();

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close menu on resize to desktop
  useEffect(() => {
    if (!isMobile) setIsMenuOpen(false);
  }, [isMobile]);

  const menuItems = [
    { label: 'Home', path: '/' },
    { label: 'Soluciones', path: '/soluciones' },
    { label: 'Planes y Precios', path: '/planes' },
    { label: 'Solicitá una Demo', path: '/demo' },
    { label: 'Contactar con Soporte', href: `mailto:${settings.contact_email || 'mispedidosapp@gmail.com'}` }
  ];

  const brandParts = (settings.brand_name || 'mispedidos app').toUpperCase().split(' ');

  const handleNavClick = () => {
    setIsMenuOpen(false);
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black border-b border-[#333333]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">

            {/* Logo */}
            <Link
              to="/"
              className="text-xl md:text-2xl font-black tracking-tighter flex-shrink-0"
              style={{ fontFamily: 'Unbounded, Montserrat, sans-serif' }}
              data-testid="agency-logo"
            >
              <span className="text-[#E9FF60]">{brandParts[0] || 'MIS'}</span>
              <span className="text-white">{brandParts[1] || 'PEDIDOS'}</span>
              {brandParts[2] && <span className="text-[#E9FF60]"> {brandParts[2]}</span>}
            </Link>

            {/* Desktop Menu — solo visible en pantallas >= 1024px */}
            {!isMobile && (
              <div className="flex items-center gap-5">
                {menuItems.map((item, index) => (
                  item.href ? (
                    <a
                      key={index}
                      href={item.href}
                      className="text-white hover:text-[#E9FF60] transition-colors text-sm font-medium whitespace-nowrap"
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {item.label}
                    </a>
                  ) : (
                    <Link
                      key={index}
                      to={item.path}
                      className="text-white hover:text-[#E9FF60] transition-colors text-sm font-medium whitespace-nowrap"
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {item.label}
                    </Link>
                  )
                ))}
                <Button
                  onClick={() => navigate('/login')}
                  className="bg-[#E9FF60] hover:bg-[#f0ff80] text-black font-bold flex-shrink-0"
                  size="sm"
                  data-testid="nav-login"
                >
                  <LogIn className="w-4 h-4 mr-2" />
                  Iniciar Sesión
                </Button>
              </div>
            )}

            {/* Mobile: botón login compacto + hamburguesa */}
            {isMobile && (
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => navigate('/login')}
                  className="bg-[#E9FF60] hover:bg-[#f0ff80] text-black font-bold text-xs px-3 py-1 h-8"
                  size="sm"
                  data-testid="nav-login"
                >
                  <LogIn className="w-3 h-3 mr-1" />
                  Ingresar
                </Button>
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="p-2 text-white hover:text-[#E9FF60] transition-colors"
                  data-testid="mobile-menu-button"
                  aria-label="Abrir menú"
                >
                  {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile Drawer */}
      {isMobile && isMenuOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={handleNavClick}
        >
          {/* Overlay */}
          <div className="absolute inset-0 bg-black/80" />

          {/* Menu panel */}
          <div
            className="absolute top-16 left-0 right-0 bg-[#0a0a0a] border-b border-[#333333] shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="mobile-menu"
          >
            <div className="flex flex-col px-6 py-4">
              {menuItems.map((item, index) => (
                item.href ? (
                  <a
                    key={index}
                    href={item.href}
                    className="text-gray-300 hover:text-[#E9FF60] transition-colors text-base font-medium py-3 border-b border-[#1a1a1a] last:border-0"
                    onClick={handleNavClick}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {item.label}
                  </a>
                ) : (
                  <Link
                    key={index}
                    to={item.path}
                    className="text-gray-300 hover:text-[#E9FF60] transition-colors text-base font-medium py-3 border-b border-[#1a1a1a] last:border-0"
                    onClick={handleNavClick}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {item.label}
                  </Link>
                )
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AgencyNavbar;
